import { useEffect, useState } from 'react';
import { supabase, NewsPost, Project, SocialLink } from '../lib/supabase';
import type { Episode } from '../types/episodes';

interface HomePageProps {
  onNavigateToEpisode: (slug: string) => void;
  onNavigateToAllEpisodes: () => void;
  onNavigateToNews: () => void;
  onNavigateToNewsArticle: (id: string) => void;
}
import HeroCarousel from '../components/HeroCarousel';
import EpisodesTeaser from '../components/EpisodesTeaser';
import PodcastApplicationSection from '../components/PodcastApplicationSection';
import NewsTeaser from '../components/NewsTeaser';
import AboutTeaser from '../components/AboutTeaser';
import JoinWFPSection from '../components/JoinWFPSection';
import GetSocialSection from '../components/GetSocialSection';
import MetaTags from '../components/MetaTags';

export default function HomePage({ onNavigateToEpisode, onNavigateToAllEpisodes, onNavigateToNews, onNavigateToNewsArticle }: HomePageProps) {
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [newsPosts, setNewsPosts] = useState<NewsPost[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [episodesRes, newsRes, projectsRes, socialRes] = await Promise.all([
        supabase
          .from('episodes')
          .select('slug,title,description,summary,podcast_number,game_project,storage_root,carousel_slide_1080,youtube_thumbnail_1080p,game_thumbnail_460x215')
          .order('podcast_number', { ascending: false })
          .limit(25),
        supabase
          .from('app_news_posts')
          .select('id,title,slug,excerpt,cover_image,published_at,author,author_image')
          .order('published_at', { ascending: false })
          .limit(5),
        supabase
          .from('app_projects')
          .select('id,title,slug,order_index,cover_image,summary')
          .order('order_index', { ascending: true }),
        supabase
          .from('app_social_links')
          .select('id,label,url,order_index,icon')
          .order('order_index', { ascending: true }),
      ]);

      if (episodesRes.data) {
        const shuffled = [...episodesRes.data].sort(() => Math.random() - 0.5);
        setEpisodes(shuffled);
      }
      if (newsRes.data) setNewsPosts(newsRes.data);
      if (projectsRes.data) setProjects(projectsRes.data);
      if (socialRes.data) setSocialLinks(socialRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="font-press-start text-yellow-400 text-2xl mb-4 animate-pulse">
            Loading...
          </div>
          <p className="text-gray-400">Preparing your indie game dev experience</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <MetaTags
        title="yOKzONa - Indie Game Dev & Viral Formula Expert | Waiting For Players Podcast"
        description="Indie game developer, entrepreneur, and host of Waiting For Players podcast. Helping devs make games go viral with 20+ years of entrepreneurial knowledge. Get expert insights on game marketing, viral strategies, and indie game publishing."
        url="https://yokzona.com/"
        keywords="indie game development, game dev podcast, Waiting For Players, viral games, yOKzONa, game marketing, indie game publisher, game development strategies, indie dev, game entrepreneurship, viral game formula"
      />
      <HeroCarousel episodes={episodes} onNavigateToEpisode={onNavigateToEpisode} />
      <EpisodesTeaser episodes={episodes} onNavigateToEpisode={onNavigateToEpisode} onNavigateToAllEpisodes={onNavigateToAllEpisodes} />
      <PodcastApplicationSection />
      <NewsTeaser />
      <AboutTeaser />
      <JoinWFPSection />
      <GetSocialSection socialLinks={socialLinks} />
    </div>
  );
}
